import { Box, Grid, VisuallyHidden } from '@chakra-ui/react';
import { NoReviewsAlert } from 'components/reviews/NoReviewsAlert';
import type { ProductReviewsIntroProps } from 'components/reviews/ProductReviewsIntro';
import { ProductReviewsIntro } from 'components/reviews/ProductReviewsIntro';
import { ProductReviewsPaginated } from 'components/reviews/ProductReviewsPaginated';
import type { FilterNames } from 'components/reviews/ReviewFilters';
import { ReviewFilters } from 'components/reviews/ReviewFilters';
import { useRatingsOverview } from 'hooks/reviews/useRatingsOverview';
import { useReviewedProducts } from 'hooks/reviews/useReviewedProducts';
import { useReviews } from 'hooks/reviews/useReviews';
import { useEffect, useRef, useState } from 'react';
import type { JunipReviewRating } from 'types/reviews';
import { aggregateProductRatings } from 'utils/reviews/aggregateProductRatings';

import { MIN_NUM_OF_REVIEWS } from '../constants';

export interface ProductReviewsInnerProps {
  productSlugs?: string[];
  overline?: ProductReviewsIntroProps['overline'];
}

interface Filters {
  product?: string;
  rating?: JunipReviewRating;
  age?: string | null;
  withMedia?: boolean;
}

export function ProductReviewsInner({
  productSlugs,
  overline,
}: ProductReviewsInnerProps) {
  const isAllProducts = !productSlugs?.length;

  const { data: reviewedProducts } = useReviewedProducts({
    slugsToFilter: isAllProducts ? undefined : productSlugs,
  });

  const [filters, setFilters] = useState<Filters>({});
  const [filtersForQuery, setFiltersForQuery] = useState<Filters>({});
  const [showAlert, setShowAlert] = useState(false);
  const [pageNumber, setPageNumber] = useState<number>(1);

  const reviewsContainerRef = useRef<HTMLDivElement>(null);

  // Dropdown options are the current product set (main slugs on the all-reviews
  // page, otherwise the slugs passed in), limited to products that have reviews
  const productList =
    reviewedProducts?.filter((item) => (item.ratingCount ?? 0) > 0) ?? [];
  const showReviewedProduct = isAllProducts || productList.length > 1;

  // When no product is selected in the dropdown, results are filtered by this list
  const allProductIds = productList.map((item) => item.id.toString());

  // If one product is selected that will take priority, otherwise we filter by all products in the current set
  const productFilters = filtersForQuery.product
    ? [filtersForQuery.product]
    : allProductIds;

  const { reviews, submitReviewUrl, product } = useReviews({
    productSlugs,
    productFilters,
    ratingFilter: filtersForQuery.rating,
    withMediaFilter: filtersForQuery.withMedia,
    ageFilter: filtersForQuery.age,
  });

  const { data: overview } = useRatingsOverview();
  const aggregatedRatings =
    productSlugs && productSlugs.length > 1
      ? aggregateProductRatings(reviewedProducts ?? [])
      : undefined;
  const stats = product ?? aggregatedRatings ?? overview;

  const totalReviews = stats?.ratingCount || 0;
  const averageScore = stats?.ratingAverage || 0;
  const ratingDistribution = stats?.ratingDistribution || undefined;
  const recommendedPercentage =
    (product ?? aggregatedRatings)?.recommendedPercentage || undefined;
  const activeTraits = product?.activeTraits || undefined;

  const paginatedReviews = reviews?.paginatedReviews || [];

  const filteredReviewCount = paginatedReviews.reduce(
    (count, page) => count + page.length,
    0,
  );

  const filtersToShow: FilterNames[] =
    isAllProducts || productList.length > 1
      ? ['product', 'rating', 'age', 'withMedia']
      : ['rating', 'age', 'withMedia'];

  const handlePaginationChange = (pageNum: number) => {
    setPageNumber(pageNum);

    if (reviewsContainerRef?.current) {
      reviewsContainerRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const setFilterAndQuery = (
    filterName: FilterNames,
    value: string | null | boolean,
    resetFilters?: FilterNames[],
  ) => {
    setPageNumber(1);

    const filtersToSet = {
      ...filters,
      [filterName]: value,
    };

    if (value === null) {
      delete filtersToSet[filterName];
    }

    if (resetFilters) {
      resetFilters.forEach((item: FilterNames) => {
        delete filtersToSet[item];
      });
    }

    setFilters(filtersToSet);
    setFiltersForQuery(filtersToSet);

    if (showAlert) {
      setShowAlert(false);
    }
  };

  const handleResetAll = () => {
    setPageNumber(1);
    setFilters({});
    setFiltersForQuery({});
    setShowAlert(false);

    // The reset control unmounts itself, so focus would otherwise drop to body
    reviewsContainerRef.current?.focus({ preventScroll: true });
  };

  useEffect(() => {
    if (reviews?.paginatedReviews.length === 0) {
      setShowAlert(true);
      setFiltersForQuery({});
    }
  }, [reviews, showAlert]);

  if (!totalReviews || totalReviews < MIN_NUM_OF_REVIEWS) {
    return null;
  }

  return (
    <Grid
      templateColumns={{ base: '1fr', lg: 'repeat(12, 1fr)' }}
      rowGap="sm"
      columnGap="sm"
    >
      <Box gridColumn={{ base: 'auto', lg: 'span 4' }}>
        <Box position={{ lg: 'sticky' }} top="2xl" pr={{ lg: 'lg' }}>
          <ProductReviewsIntro
            totalReviews={totalReviews}
            averageScore={averageScore}
            showAllProducts={isAllProducts}
            submitReviewUrl={showReviewedProduct ? undefined : submitReviewUrl}
            ratingDistribution={ratingDistribution}
            activeTraits={activeTraits}
            recommendedPercentage={recommendedPercentage}
            isMultiProduct={productList.length > 1}
            ratingFilter={filtersForQuery.rating}
            setRatingFilter={(rating) => setFilterAndQuery('rating', rating)}
            overline={overline}
          />
        </Box>
      </Box>
      <Box
        as="section"
        aria-label="Reviews"
        ref={reviewsContainerRef}
        tabIndex={-1}
        _focus={{ outline: 'none' }}
        _focusVisible={{ boxShadow: 'outline' }}
        gridColumn={{ base: 'auto', lg: 'span 8' }}
        minWidth={0}
      >
        <VisuallyHidden aria-live="polite">
          {`Showing ${filteredReviewCount} ${
            filteredReviewCount === 1 ? 'review' : 'reviews'
          }`}
        </VisuallyHidden>
        <ReviewFilters
          productList={productList}
          filtersToShow={filtersToShow}
          product={filters.product}
          rating={filters.rating}
          age={filters.age}
          withMedia={filters.withMedia}
          onProductChange={(value) => setFilterAndQuery('product', value)}
          onRatingChange={(value) => setFilterAndQuery('rating', value)}
          onAgeChange={(value) => setFilterAndQuery('age', value)}
          onWithMediaChange={(value) => setFilterAndQuery('withMedia', value)}
          onResetFilters={handleResetAll}
        />
        {showAlert && <NoReviewsAlert handleResetFilters={handleResetAll} />}
        <ProductReviewsPaginated
          paginatedReviews={paginatedReviews}
          pageNumber={pageNumber}
          showProduct={showReviewedProduct}
          handlePaginationChange={handlePaginationChange}
        />
      </Box>
    </Grid>
  );
}
